// ABOUTME: Tab component for displaying email campaign steps in campaign detail view
// ABOUTME: Shows step sequence, timing, email templates, and optional KB articles with preview functionality

import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { CampaignStepResponse } from '../../types/emailCampaign';
import { getKBArticle, getKBVideo, getCurrentAccessToken } from '../../services/api';
import { sanitizeKBHtml } from '../../utils/sanitize';
import { errorTint } from '../../theme/tokens';
import axios from 'axios';

interface CampaignStepsTabProps {
  steps: CampaignStepResponse[];
}

interface EmailTemplate {
  id: number;
  templateKey: string;
  subjectLine: string;
  htmlContent: string;
  plainTextContent: string;
}

interface KBArticle {
  id: number;
  title: string;
  content: string;
  description: string | null;
  categoryName: string | null;
}

const CampaignStepsTab: React.FC<CampaignStepsTabProps> = ({ steps }) => {
  const theme = useTheme();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState<'template' | 'article'>('template');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<EmailTemplate | KBArticle | null>(null);

  // Parse video shortcodes [video:123] and replace with embedded players
  const parseVideoShortcodes = async (content: string): Promise<string> => {
    const videoShortcodeRegex = /\[video:(\d+)\]/g;
    const matches = Array.from(content.matchAll(videoShortcodeRegex));

    let parsedContent = content;

    for (const match of matches) {
      const videoId = parseInt(match[1]);
      try {
        const video = await getKBVideo(videoId);
        const playbackUrl = video.playbackUrl;

        // Determine video type and create appropriate embed
        const isYouTube = playbackUrl.includes('youtube.com') || playbackUrl.includes('youtu.be');
        const isVimeo = playbackUrl.includes('vimeo.com');

        let embedHtml = '';

        if (isYouTube) {
          const youtubeId = playbackUrl.split('v=')[1]?.split('&')[0] || playbackUrl.split('/').pop();
          embedHtml = `
            <div style="position: relative; padding-bottom: 56.25%; height: 0; margin: 20px 0;">
              <iframe
                src="https://www.youtube.com/embed/${youtubeId}?enablejsapi=1&rel=0&modestbranding=1&showinfo=0"
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 8px;"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
            <p style="text-align: center; margin-top: -10px; margin-bottom: 20px;">
              <strong>${video.title}</strong>
            </p>
          `;
        } else if (isVimeo) {
          const vimeoId = playbackUrl.split('/').pop();
          embedHtml = `
            <div style="position: relative; padding-bottom: 56.25%; height: 0; margin: 20px 0;">
              <iframe
                src="https://player.vimeo.com/video/${vimeoId}"
                style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 8px;"
                allow="autoplay; fullscreen; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
            <p style="text-align: center; margin-top: -10px; margin-bottom: 20px;">
              <strong>${video.title}</strong>
            </p>
          `;
        } else {
          // S3 or direct video URL
          embedHtml = `
            <div style="margin: 20px 0;">
              <video controls style="width: 100%; border-radius: 8px;">
                <source src="${playbackUrl}" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <p style="text-align: center; margin-top: 10px;">
                <strong>${video.title}</strong>
              </p>
            </div>
          `;
        }

        parsedContent = parsedContent.replace(match[0], embedHtml);
      } catch (err) {
        console.error(`Failed to load video ${videoId}:`, err);
        // Replace with error message
        parsedContent = parsedContent.replace(
          match[0],
          `<div style="padding: 20px; background: ${errorTint[50]}; border: 1px solid ${errorTint[200]}; border-radius: 4px; margin: 20px 0;">
            <strong>Video not found (ID: ${videoId})</strong>
          </div>`
        );
      }
    }

    return parsedContent;
  };

  const handlePreviewTemplate = async (templateId: number) => {
    setPreviewType('template');
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);

    try {
      const token = getCurrentAccessToken();
      const response = await axios.get<EmailTemplate>(
        `${import.meta.env.VITE_API_URL}/api/admin/email-templates/by-id/${templateId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setPreviewData(response.data);
    } catch (err: unknown) {
      console.error('Error fetching template:', err);
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setPreviewError(message || 'Failed to load email template');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewArticle = async (articleId: number) => {
    setPreviewType('article');
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);

    try {
      const article = await getKBArticle(articleId);
      // Parse video shortcodes in the content
      const parsedContent = await parseVideoShortcodes(article.content);
      setPreviewData({ ...article, content: parsedContent } as unknown as KBArticle);
    } catch (err: unknown) {
      console.error('Error fetching KB article:', err);
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setPreviewError(message || 'Failed to load KB article');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewData(null);
    setPreviewError(null);
  };

  if (!steps || steps.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No steps configured for this campaign.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Step</strong></TableCell>
              <TableCell><strong>Day Offset</strong></TableCell>
              <TableCell><strong>Send Time</strong></TableCell>
              <TableCell><strong>Email Template</strong></TableCell>
              <TableCell><strong>KB Article</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {steps
              .sort((a, b) => a.dayOffset - b.dayOffset)
              .map((step, index) => (
                <TableRow key={step.id} hover>
                  <TableCell>
                    <Chip
                      label={index + 1}
                      size="small"
                      color="primary"
                      sx={{ minWidth: 32 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      Day {step.dayOffset}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {step.sendTime}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {step.templateId ? (
                          `Template ${step.templateId}`
                        ) : (
                          <Box component="em" sx={{ color: theme.palette.grey[500] }}>None</Box>
                        )}
                      </Typography>
                      {step.templateId && (
                        <IconButton
                          size="small"
                          onClick={() => handlePreviewTemplate(step.templateId!)}
                          title="Preview email template"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {step.kbArticleTitle ? (
                          step.kbArticleTitle
                        ) : (
                          <Box component="em" sx={{ color: theme.palette.grey[500] }}>None</Box>
                        )}
                      </Typography>
                      {step.kbArticleId && (
                        <IconButton
                          size="small"
                          onClick={() => handlePreviewArticle(step.kbArticleId!)}
                          title="Preview KB article"
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {previewType === 'template' ? 'Email Template Preview' : 'KB Article Preview'}
        </DialogTitle>
        <DialogContent>
          {previewLoading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}

          {previewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {previewError}
            </Alert>
          )}

          {!previewLoading && !previewError && previewData && (
            <>
              {previewType === 'template' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Subject: {(previewData as EmailTemplate).subjectLine}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Template Key: {(previewData as EmailTemplate).templateKey}
                  </Typography>

                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      HTML Preview:
                    </Typography>
                    <Paper
                      sx={{ p: 2, maxHeight: '400px', overflow: 'auto', bgcolor: theme.palette.grey[100] }}
                      dangerouslySetInnerHTML={{ __html: sanitizeKBHtml((previewData as EmailTemplate).htmlContent) }}
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Plain Text:
                    </Typography>
                    <Paper sx={{ p: 2, maxHeight: '200px', overflow: 'auto', bgcolor: theme.palette.grey[100] }}>
                      {/* Monospace code block sizing paired with the (out-of-scope) fontFamily override, not a Typography variant */}
                      <Box component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {(previewData as EmailTemplate).plainTextContent}
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              )}

              {previewType === 'article' && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {(previewData as KBArticle).title}
                  </Typography>
                  {(previewData as KBArticle).categoryName && (
                    <Chip label={(previewData as KBArticle).categoryName} size="small" sx={{ mb: 2 }} />
                  )}

                  {(previewData as KBArticle).description && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: theme.palette.grey[100], borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Description:
                      </Typography>
                      <Typography variant="body2">
                        {(previewData as KBArticle).description}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Content:
                    </Typography>
                    <Paper
                      sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: sanitizeKBHtml((previewData as KBArticle).content) }}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignStepsTab;
